/**
 * The "Hear From Our People" section of the Ryerson Careers page
 * (`src/pages/about-us/careers/index.tsx`): employee quotes, each beside a YouTube video
 * (`./QuoteWithVideo.tsx`). Below `lg` the slides sit in a `Carousel` with no heading (the page's
 * `AllSections` accordion tab shows the title); from `lg` up they are stacked under an `h1` title.
 * Copy: `./Content.tsx` → `HearFromOurContent`.
 */
import React from "react";
import { HearFromOurContent } from "./Content";
import { useApplication } from "@ryerson/frontend.application";
import { Media } from "@ryerson/frontend.next";
import { Carousel } from "@ryerson/frontend.carousel";
import QuoteWithVideo from "./QuoteWithVideo";
import { ContentSection } from "@ryerson/frontend.layout";
import styled from "@emotion/styled";
import rgba from "polished/lib/color/rgba";
import { Typography } from "@ryerson/frontend.typography";
import { useTheme } from "@ryerson/frontend.theme";
import { css } from "@emotion/react";

/** One employee testimonial. */
export type Slide = {
	quote: string;
	/** Employee's name; also the slide's React key, so names must be unique within `slides`. */
	name: string;
	/** Employee's role, shown under the name. */
	title: string;
	/** Video poster image. */
	imageUrl: string;
	/**
	 * Passed to `Video` from `@ryerson/frontend.media`; see `./QuoteWithVideo.tsx` →
	 * `QuoteWithVideoProps`.
	 */
	videoId: string;
};

/** One language's section copy. */
export type HearFromOurPeopleContent = {
	/** Section heading, drawn from `lg` up only. */
	title: string;
	slides: Slide[];
};

/** Props for the section. */
export type HearFromOurPeopleProps = {
	content: HearFromOurPeopleContent;
};

const HeaderContent = styled.div`
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${rgba(theme.colors.primary.secondaryBrand, 0.1)};
		`;
	}}
`;

// The values below are quoted strings (the last quote is unclosed), which CSS doesn't accept for
// these properties, so the 665 px height and hidden overflow don't apply.
const DesktopSection = styled.div`
    display : 'block';
    width : '100%';
    height : '665px';
    overflow : 'hidden;
`;

/**
 * Renders the section. Without `content` it falls back to {@link HearFromOurContent} in the
 * visitor's language; the Careers page always passes it.
 */
const HearFromOurPeople: React.FC<HearFromOurPeopleProps> = ({ content }) => {
	const {
		localization: { language },
	} = useApplication();
	const { theme } = useTheme();

	if (!content) content = HearFromOurContent[language];

	return (
		<>
			<Media lessThan="lg">
				<ContentSection hPadding="0px" vPadding="40px">
					<Carousel header={false} language={language}>
						{content.slides.map((slide) => (
							<QuoteWithVideo
								key={slide.name}
								quote={slide.quote}
								name={slide.name}
								title={slide.title}
								imageUrl={slide.imageUrl}
								videoId={slide.videoId}
							/>
						))}
					</Carousel>
				</ContentSection>
			</Media>
			<Media greaterThanOrEqual="lg">
				<ContentSection vPadding="80px" type="secondary">
					<DesktopSection>
						<HeaderContent theme={theme}>
							<Typography
								variant="h1"
								css={css`
									margin-bottom: 40px;
								`}
							>
								{content.title}
							</Typography>
						</HeaderContent>
						{content.slides.map((slide) => (
							<QuoteWithVideo
								key={slide.name}
								quote={slide.quote}
								name={slide.name}
								title={slide.title}
								imageUrl={slide.imageUrl}
								videoId={slide.videoId}
							/>
						))}
					</DesktopSection>
				</ContentSection>
			</Media>
		</>
	);
};

export default HearFromOurPeople;
